import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Award, AlertTriangle } from "lucide-react";

import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";

import type { SilaeFormData } from "@/types/silae";
import {
  createEmptyBonus,
  createEmptyBranch,
} from "@/hooks/useSilaeWizardFormik";
import BonusConfig from "../BonusConfig";

interface Step4ConfigurationElseProps {
  formik: FormikProps<SilaeFormData>;
}

/**
 * Étape 4 : Configuration de la branche ELSE avec support multi-primes via tabs
 * Réutilise la même architecture que Step3ConfigurationIF
 */
export const Step4ConfigurationElse: React.FC<Step4ConfigurationElseProps> = ({
  formik,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("bonus-0");

  // S'assurer que elseBranch existe
  useEffect(() => {
    if (!formik.values.elseBranch) {
      formik.setFieldValue("elseBranch", createEmptyBranch());
    }
  }, [formik.values.elseBranch, formik]);

  const bonuses = useMemo(
    () => formik.values.elseBranch?.bonuses || [],
    [formik.values.elseBranch?.bonuses],
  );

  const addBonus = useCallback(() => {
    const newBonus = createEmptyBonus();

    // Si c'est la deuxième prime ou plus, activer automatiquement les conditions
    if (bonuses.length >= 1) {
      newBonus.hasConditions = true;
      // Ajouter une condition par défaut
      newBonus.conditions = [
        {
          id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: "VAR_SALARY_BASE",
          silaeVariable: "bul.salairedebase",
          conditionOperator: ">=",
          conditionValue: "",
        },
      ];

      // Activer aussi les conditions pour toutes les primes existantes
      const updatedBonuses = bonuses.map((bonus) => {
        if (!bonus.hasConditions) {
          return {
            ...bonus,
            hasConditions: true,
            conditions:
              bonus.conditions.length === 0
                ? [
                    {
                      id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      type: "VAR_SALARY_BASE",
                      silaeVariable: "bul.salairedebase",
                      conditionOperator: ">=",
                      conditionValue: "",
                    },
                  ]
                : bonus.conditions,
          };
        }
        return bonus;
      });

      updatedBonuses.push(newBonus);
      formik.setFieldValue("elseBranch.bonuses", updatedBonuses);
    } else {
      const updatedBonuses = [...bonuses, newBonus];
      formik.setFieldValue("elseBranch.bonuses", updatedBonuses);
    }

    setActiveTab(`bonus-${bonuses.length}`);
  }, [bonuses, formik]);

  const removeBonus = useCallback(
    (index: number) => {
      if (bonuses.length <= 1) return;
      const updatedBonuses = bonuses.filter((_, i) => i !== index);
      formik.setFieldValue("elseBranch.bonuses", updatedBonuses);
      if (activeTab === `bonus-${index}`) {
        setActiveTab("bonus-0");
      }
    },
    [bonuses, formik, activeTab],
  );

  return (
    <Card className="space-y-6 p-4 gap-4 rounded-3xl">
      {/* Header avec thème ELSE (orange/yellow) */}
      <Card className="border-yellow-400 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-950/40 mb-4">
        <CardHeader className="bg-yellow-50 dark:bg-orange-950/20 pb-4">
          <CardTitle className="flex items-center gap-2 text-orange-900 dark:text-orange-100">
            <AlertTriangle className="h-5 w-5" />
            {t("step4ConfigurationElse.title")}
          </CardTitle>
          <CardDescription className="text-orange-700 dark:text-orange-300">
            {t("step4ConfigurationElse.description")}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Gestion des primes ELSE */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">
            {t("step4ConfigurationElse.bonusesManagement")}
          </h3>
        </div>
        <Button
          type="button"
          onClick={addBonus}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("step4ConfigurationElse.addBonus")}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
          {bonuses.map((bonus, index) => (
            <div key={index} className="flex items-center gap-1">
              <TabsTrigger
                value={`bonus-${index}`}
                className="flex items-center gap-2"
              >
                <Award className="h-3 w-3" />
                {bonus.libelle ||
                  `${t("step4ConfigurationElse.bonus")} ${index + 1}`}
                {bonuses.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBonus(index);
                    }}
                    className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </TabsTrigger>
            </div>
          ))}
        </TabsList>

        {bonuses.map((_, index) => (
          <TabsContent key={index} value={`bonus-${index}`}>
            <BonusConfig
              formik={formik}
              bonusIndex={index}
              totalBonuses={bonuses.length}
              branch="elseBranch"
            />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
};
